import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import gsap from 'gsap';
import { buildApiUrl } from '../../common/api';
import { MACRO_ZONE, TIPO_LABELS, macroLabel } from '../../common/utils/geoItalia';
import ItaliaMapView from '../components/italia3d/ItaliaMapView';
import { REGION_LABELS } from '../components/italia3d/regionConfig';
import { trackPartnerEvent } from '../hooks/useCandidaturaAnalytics';
import NewsletterSignup from '../components/NewsletterSignup';
import DocumentHead from '../components/DocumentHead';
import PartnerRecruitmentBanner from '../components/PartnerRecruitmentBanner';
import { useShopConfig } from '../hooks/useShopConfig';
import preferitiService from '../api/preferitiService';
import clienteAuthService from '../api/authService';
import ordiniService from '../api/clienteService';
import { addToCarrello } from '../hooks/carrelloStorage';
import IndirizzoForm from '../components/IndirizzoForm';
import '../../common/theme/tokens.css';
import { getCategoriaGallery, getCategoriaImage, logo, heroClienti, camioncino } from '../utils/shopMedia';

function euro(v) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

function TipoBadge({ tipo }) {
  const colors = { fresco: 'bg-blue-100 text-blue-800', secco: 'bg-amber-100 text-amber-800', fragile: 'bg-purple-100 text-purple-800' };
  return (
    <span className={`mang-badge ${colors[tipo] || 'bg-gray-100 text-gray-700'}`}>
      {TIPO_LABELS[tipo] || tipo}
    </span>
  );
}

function ProductRow({ categoria, baseUrl, isActive, onVisible, onSelect, preferito, onTogglePreferito, index, interesseOpenId, setInteresseOpenId, interesseNota, setInteresseNota, interesseEmail, setInteresseEmail, interesseInvio, onSegnalaInteresse }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onVisible(categoria); },
      { threshold: 0.55, rootMargin: '-20% 0px -20% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [categoria, onVisible]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    gsap.fromTo(el, { opacity: 0, x: -24 }, {
      opacity: 1,
      x: 0,
      duration: 0.5,
      delay: index * 0.06,
      ease: 'power2.out',
    });
  }, [index]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !isActive) return;
    gsap.to(el, { boxShadow: '0 8px 28px rgba(246,207,72,0.35)', duration: 0.35, ease: 'power2.out' });
    return () => { gsap.to(el, { boxShadow: '0 2px 12px rgba(0,0,0,0.08)', duration: 0.3 }); };
  }, [isActive]);

  return (
    <article
      ref={ref}
      className={`mang-card overflow-hidden flex flex-col sm:flex-row w-full cursor-pointer transition-shadow
        ${isActive ? 'ring-2 ring-[#F6CF48] shadow-lg' : 'hover:shadow-md'}
        ${!categoria.disponibile ? 'opacity-70' : ''}`}
      onClick={() => onSelect(categoria)}
    >
      <div className="sm:w-48 md:w-56 shrink-0 relative">
        <img
          src={getCategoriaImage(categoria, baseUrl)}
          alt={categoria.nome}
          className="w-full h-40 sm:h-full sm:min-h-[140px] object-cover"
        />
        {categoria.provenienza?.nome && (
          <span className="absolute bottom-2 left-2 text-[10px] bg-white/90 text-[#c78244] px-2 py-0.5 rounded-full font-semibold">
            📍 {categoria.provenienza.nome}
          </span>
        )}
      </div>

      <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
            <h3 className="font-bold text-lg text-gray-800">{categoria.nome}</h3>
            <button
              type="button"
              className="text-xl shrink-0 hover:scale-110 transition-transform"
              aria-label={preferito ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
              onClick={(e) => { e.stopPropagation(); onTogglePreferito(categoria.id); }}
            >
              {preferito ? '❤️' : '🤍'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {(categoria.tipi_spedizione || []).map((t) => <TipoBadge key={t} tipo={t} />)}
            {categoria.provenienza?.macro && (
              <span className="mang-badge bg-[#f5e6d0] text-[#9a5f2f]">
                {macroLabel(categoria.provenienza.macro)}
              </span>
            )}
            {!categoria.disponibile && (
              <>
                <span className="mang-badge mang-badge-warning">Fuori raggio consegna</span>
                <button
                  type="button"
                  className="mang-badge bg-amber-50 text-amber-700 border border-amber-300 cursor-pointer hover:bg-amber-100"
                  onClick={(e) => { e.stopPropagation(); setInteresseOpenId(interesseOpenId === categoria.id ? null : categoria.id); }}
                >
                  Segnala interesse
                </button>
                {interesseOpenId === categoria.id && (
                  <div
                    className="w-full mt-2 bg-white border border-amber-200 rounded-xl p-3 shadow-lg z-30"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {interesseInvio[categoria.id] === 'ok' ? (
                      <p className="text-xs text-green-700 font-semibold">✓ Interesse registrato. Ti aggiorneremo!</p>
                    ) : (
                      <>
                        <p className="text-xs font-semibold text-amber-800 mb-2">Vuoi ricevere aggiornamenti quando sarà disponibile nella tua zona?</p>
                        <input
                          type="email"
                          placeholder="La tua email (opzionale)"
                          className="border rounded-lg px-2 py-1 text-xs w-full mb-2"
                          value={interesseEmail}
                          onChange={(e) => setInteresseEmail(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <textarea
                          placeholder="Nota (opzionale)"
                          className="border rounded-lg px-2 py-1 text-xs w-full mb-2"
                          rows={2}
                          value={interesseNota}
                          onChange={(e) => setInteresseNota(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="flex-1 text-xs bg-amber-500 text-white rounded-lg py-1 hover:bg-amber-600"
                            onClick={(e) => { e.stopPropagation(); onSegnalaInteresse(categoria); }}
                          >
                            Invia segnalazione
                          </button>
                          <button
                            type="button"
                            className="text-xs border border-gray-200 text-gray-500 rounded-lg px-3 py-1"
                            onClick={(e) => { e.stopPropagation(); setInteresseOpenId(null); }}
                          >
                            Annulla
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 line-clamp-2">{categoria.descrizione}</p>
        </div>
        <div className="flex items-end justify-between mt-4 pt-3 border-t border-gray-100">
          <div>
            {categoria.voto_piattaforma > 0 && (
              <p className="text-xs text-[#c78244]">★ {Number(categoria.voto_piattaforma).toFixed(1)} Mangitalia</p>
            )}
          </div>
          <div className="text-right">
            <span className="text-xl font-bold text-[#c78244]">{euro(categoria.prezzo_piatto)}</span>
            <span className="text-xs text-gray-400 block">+ trasporto</span>
            <span className="text-xs text-[#9a5f2f] font-semibold block mt-1">Scopri il prodotto →</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function ProductDetail({ categoria, baseUrl, onClose, delivery, catalogoLive }) {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const [quantita, setQuantita] = useState(1);
  const [indirizzo, setIndirizzo] = useState({
    via: delivery?.via || '',
    citta: delivery?.citta || '',
    cap: delivery?.cap || '',
    lat: delivery?.lat || null,
    lng: delivery?.lng || null,
  });
  const [dataConsegna, setDataConsegna] = useState('');
  const [preventivo, setPreventivo] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const tl = gsap.timeline();
    if (heroRef.current) {
      tl.fromTo(heroRef.current, { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' });
    }
    if (contentRef.current) {
      tl.fromTo(contentRef.current, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '-=0.3');
    }
  }, []);

  useEffect(() => {
    if (!indirizzo.via || !indirizzo.citta) return;
    fetch(buildApiUrl('/api/catalogo/preventivo'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        categoria_catalogo_id: categoria.id,
        quantita,
        indirizzo_consegna: indirizzo,
        data_consegna_richiesta: dataConsegna || undefined,
      }),
    })
      .then((r) => r.json())
      .then(setPreventivo)
      .catch(() => setPreventivo(null));
  }, [categoria.id, quantita, indirizzo, dataConsegna]);

  // const aggiungiAlCarrello = () => {
  //   addToCarrello({
  //     categoria_catalogo_id: categoria.id,
  //     nome: categoria.nome,
  //     prezzo_piatto: categoria.prezzo_piatto,
  //     quantita,
  //   });
  // };

  const handleOrdine = async () => {
    if (!clienteAuthService.isAuthenticated()) {
      navigate('/area-riservata/login', { state: { from: '/catalogo' } });
      return;
    }
    setCheckoutLoading(true);
    setError('');
    try {
      const res = await ordiniService.create({
        categoria_catalogo_id: categoria.id,
        quantita,
        indirizzo_consegna: indirizzo,
        data_consegna_richiesta: dataConsegna || undefined,
      });
      const ordine = (res.data.ordini || [res.data.ordine]).filter(Boolean)[0];
      if (!ordine) throw new Error('Ordine non creato');

      const checkoutRes = await fetch(buildApiUrl('/api/cliente/pagamenti/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cliente_token')}`,
        },
        body: JSON.stringify({
          ordine_id: ordine.id,
          success_url: `${window.location.origin}/area-riservata?pagamento=ok`,
          cancel_url: `${window.location.origin}/catalogo?pagamento=cancel`,
        }),
      });
      const checkout = await checkoutRes.json();
      if (checkout.checkout_url) {
        window.location.href = checkout.checkout_url;
      } else {
        setError(checkout.message || 'Errore avvio pagamento');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Errore creazione ordine');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const totale = preventivo?.totale ?? categoria.prezzo_piatto * quantita;
  const regionId = categoria.provenienza?.id;
  const macro = categoria.provenienza?.macro || 'centro';
  const regionLabel = (regionId && REGION_LABELS[regionId]) || categoria.provenienza?.nome || 'Italia';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#FFFEFA] overflow-y-auto"
    >
      <div ref={heroRef} className="relative h-64 sm:h-80 md:h-96 overflow-hidden bg-[#FFFEFA]">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 left-4 bg-white/90 rounded-full w-10 h-10 flex items-center justify-center shadow text-gray-700 z-20"
          aria-label="Chiudi"
        >
          ←
        </button>

        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <img
            src={logo}
            alt="Mangitalia"
            className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white shadow-md p-1 object-contain"
          />
        </div>

        <div className="absolute inset-0 pt-16 pb-24 px-3 sm:px-6">
          <ItaliaMapView
            activeRegion={regionId}
            activeMacro={macro}
            filterRegion={regionId}
            expanded
            interactive={false}
            className="h-full max-w-lg mx-auto"
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-[#FFFEFA] via-[#FFFEFA]/95 to-transparent pt-12 pb-4 px-5 sm:px-6">
          <h2 className="text-xl sm:text-3xl font-bold text-gray-800 leading-tight">
            {categoria.nome}
          </h2>
          <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="text-[10px] sm:text-xs text-[#c78244] font-semibold uppercase tracking-widest">
              Provenienza
            </span>
            <span className="text-base sm:text-lg font-semibold text-[#9a5f2f]">
              {regionLabel}
            </span>
          </div>
          <Link
            to={`/candidatura?categoria=${encodeURIComponent(categoria.provenienza?.categoria || '')}&regione=${regionId || ''}`}
            onClick={() => trackPartnerEvent('partner_cta_click', { source: 'dettaglio_prodotto', regione: regionId })}
            className="inline-block mt-3 text-xs text-[#c78244] underline"
          >
            Produci in questa zona? Candidati come produttore
          </Link>
        </div>
      </div>

      <div ref={contentRef} className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 gap-2">
          {getCategoriaGallery(categoria, baseUrl).slice(0, 2).map((src) => (
            <img key={src} src={src} alt={categoria.nome} className="w-full h-36 object-cover rounded-xl" />
          ))}
        </div>
        <p className="text-gray-600 leading-relaxed">{categoria.descrizione}</p>
        <div className="flex flex-wrap gap-2">
          {(categoria.tipi_spedizione || []).map((t) => <TipoBadge key={t} tipo={t} />)}
        </div>

        {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg">{error}</div>}
        {!catalogoLive && (
          <div className="bg-[#fff8ea] border border-[#f3deb8] text-[#7a4a1f] text-sm p-3 rounded-lg space-y-3">
            <p>
              <strong>Anteprima catalogo.</strong> Esplora prodotti e preventivi; gli ordini
              saranno attivi al lancio ufficiale su tutte le categorie alimentari.
            </p>
            <NewsletterSignup sorgente="catalogo_dettaglio" interesse={categoria?.nome || 'catalogo'} compact />
          </div>
        )}
        {preventivo && !preventivo.disponibile && (
          <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded-lg">
            Non disponibile per questa consegna o data.
          </div>
        )}

        <div className="mang-card p-6 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold text-[#c78244]">{euro(categoria.prezzo_piatto)}</span>
            <div className="flex items-center gap-2">
              <label className="text-sm">Qtà</label>
              <input
                type="number"
                min={1}
                max={99}
                value={quantita}
                onChange={(e) => setQuantita(parseInt(e.target.value, 10) || 1)}
                className="w-16 border rounded-lg px-2 py-1 text-center"
              />
            </div>
          </div>

          <input
            type="date"
            className="border rounded-lg px-3 py-2 text-sm w-full"
            value={dataConsegna}
            onChange={(e) => setDataConsegna(e.target.value)}
          />
          <IndirizzoForm value={indirizzo} onChange={setIndirizzo} />

          {preventivo && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Prodotti</span><span>{euro(preventivo.importo_prodotti)}</span></div>
              <div className="flex justify-between"><span>Trasporto</span><span>{euro(preventivo.importo_trasporto)}</span></div>
              <div className="flex justify-between font-bold border-t pt-1"><span>Totale</span><span>{euro(preventivo.totale)}</span></div>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={handleOrdine}
              disabled={!catalogoLive || checkoutLoading || !indirizzo.via || !indirizzo.citta || !indirizzo.cap || (preventivo && !preventivo.disponibile)}
              className="w-full bg-[#c78244] text-white py-3.5 rounded-xl font-bold hover:opacity-90 disabled:opacity-50 text-lg transition-opacity"
            >
              {checkoutLoading
                ? 'Elaborazione…'
                : catalogoLive
                  ? `Ordina e paga ${euro(totale)}`
                  : 'Ordini in arrivo al lancio'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function EsperienzaShopContent() {
  const { id: productIdParam } = useParams();
  const { catalogo_live: catalogoLive, catalogo_preview: catalogoPreview } = useShopConfig();
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipo, setTipo] = useState('');
  const [macroZona, setMacroZona] = useState('');
  const [regione, setRegione] = useState('');
  const [delivery, setDelivery] = useState({ via: '', citta: '', cap: '', lat: null, lng: null });
  const [deliveryApplied, setDeliveryApplied] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [highlighted, setHighlighted] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preferitiIds, setPreferitiIds] = useState([]);
  const [apiError, setApiError] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const [mostraTutti, setMostraTutti] = useState(false);
  const [interesseOpenId, setInteresseOpenId] = useState(null);
  const [interesseNota, setInteresseNota] = useState('');
  const [interesseEmail, setInteresseEmail] = useState('');
  const [interesseInvio, setInteresseInvio] = useState({});
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

  useEffect(() => {
    if (clienteAuthService.isAuthenticated()) {
      preferitiService.list().then((r) => {
        setPreferitiIds((r.data.preferiti || []).map((p) => p.categoria_catalogo_id));
      }).catch(() => {});
    }
  }, []);

  const loadCatalogo = useCallback(() => {
    setLoading(true);
    setApiError('');
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (tipo) params.set('tipo', tipo);
    if (macroZona) params.set('macro_zona', macroZona);
    if (regione) params.set('regione', regione);
    if (deliveryApplied) {
      if (delivery.lat && delivery.lng) {
        params.set('lat', delivery.lat);
        params.set('lng', delivery.lng);
      } else if (delivery.via && delivery.citta) {
        params.set('indirizzo', `${delivery.via}, ${delivery.citta} ${delivery.cap}`.trim());
      }
      if (!mostraTutti) {
        params.set('solo_disponibili', '1');
      }
    }
    const qs = params.toString() ? `?${params}` : '';
    fetch(buildApiUrl(`/api/catalogo${qs}`))
      .then((r) => {
        if (!r.ok) throw new Error(`API ${r.status}`);
        return r.json();
      })
      .then((j) => setCategorie(j.data?.data || []))
      .catch(() => {
        setCategorie([]);
        setApiError(import.meta.env.DEV
          ? 'Impossibile caricare il catalogo. Verifica che Laravel sia avviato su http://127.0.0.1:8000 e che il seed sia stato eseguito.'
          : 'Impossibile caricare il catalogo. Riprova tra qualche minuto.');
      })
      .finally(() => setLoading(false));
  }, [search, tipo, macroZona, regione, deliveryApplied, delivery, mostraTutti]);

  useEffect(() => { loadCatalogo(); }, [loadCatalogo]);

  useEffect(() => {
    if (!productIdParam || categorie.length === 0) return;
    const cat = categorie.find((c) => String(c.id) === String(productIdParam));
    if (cat) setSelected(cat);
  }, [productIdParam, categorie]);

  useEffect(() => {
    if (!mobileMapOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileMapOpen]);

  const applyDelivery = () => {
    if (!delivery.via && !delivery.citta && !delivery.lat) return;
    setDeliveryApplied(true);
  };

  const geolocateDelivery = () => {
    navigator.geolocation?.getCurrentPosition((pos) => {
      setDelivery((d) => ({
        ...d,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      }));
      setDeliveryApplied(true);
    });
  };

  const handleVisible = useCallback((cat) => {
    setHighlighted(cat);
    setActiveProduct(cat.id);
  }, []);

  const togglePreferito = async (categoriaId) => {
    if (!clienteAuthService.isAuthenticated()) {
      window.location.href = '/area-riservata/login';
      return;
    }
    if (preferitiIds.includes(categoriaId)) {
      await preferitiService.remove(categoriaId);
      setPreferitiIds((ids) => ids.filter((id) => id !== categoriaId));
    } else {
      await preferitiService.add(categoriaId);
      setPreferitiIds((ids) => [...ids, categoriaId]);
    }
  };

  const mapMacro = highlighted?.provenienza?.macro || macroZona || 'centro';
  const mapRegion = highlighted?.provenienza?.id || regione || null;
  const activeFiltersCount = [search, tipo, macroZona, regione, deliveryApplied ? 'delivery' : '']
    .filter(Boolean).length;

  const handleRegionClick = (id) => {
    setRegione((r) => (r === id ? '' : id));
    setMacroZona('');
    setMobileMapOpen(false);
  };

  const handleMacroClick = (id) => {
    setMacroZona((m) => (m === id ? '' : id));
    setRegione('');
    setMobileMapOpen(false);
  };

  const clearAllFilters = () => {
    setSearch('');
    setTipo('');
    setMacroZona('');
    setRegione('');
    setDeliveryApplied(false);
  };

  const segnalaInteresse = async (categoria) => {
    try {
      const token = localStorage.getItem('cliente_token');
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(buildApiUrl('/api/cliente/interessi-copertura'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          categoria_catalogo_id: categoria.id,
          regione: delivery.citta || 'sconosciuta',
          email: interesseEmail || undefined,
          nota: interesseNota || undefined,
        }),
      });
      setInteresseInvio((prev) => ({ ...prev, [categoria.id]: 'ok' }));
      setInteresseNota('');
      setInteresseEmail('');
    } catch {
      // silently ignore
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFEFA] relative">
      <DocumentHead
        title={catalogoPreview ? 'Catalogo — Anteprima' : 'Catalogo'}
        description="Catalogo Mangitalia: eccellenze alimentari italiane selezionate per categoria e territorio."
        path="/catalogo"
      />
      {/* Header Catalogo + filtri */}
      <header className="sticky top-14 z-[800] bg-white/95 backdrop-blur shadow-sm">
        {/* Titolo e Preferiti */}
        <div className="relative overflow-hidden bg-[#c78244]">
          <div className="absolute inset-0 mang-gradient opacity-90" />
          <div className="relative px-3 py-2.5 sm:px-4 sm:py-3 text-white">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div>
                <h1 className="text-base sm:text-lg font-bold" data-tour="catalogo-titolo">
                  {catalogoPreview ? 'Catalogo alimentare — Anteprima' : 'Catalogo alimentare'}
                </h1>
                <p className="text-[11px] opacity-90">
                  {loading ? 'Caricamento...' : `${categorie.length} categorie · formaggi, salumi, olio…`}
                </p>
              </div>
              <Link to="/preferiti" className="text-xs sm:text-sm bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full font-semibold transition-colors flex items-center gap-1.5 border border-white/40 backdrop-blur-sm shadow-sm">
                <span>❤️</span> <span className="hidden sm:inline">Preferiti</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-3 py-2 lg:px-4 lg:py-4">
          {/* Mobile: barra compatta */}
          <div className="flex gap-2 lg:hidden">
            <input
              type="search"
              placeholder="Cerca…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#F6CF48] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                ${filtersOpen || activeFiltersCount > 0
                  ? 'bg-[#c78244] text-white border-[#c78244]'
                  : 'border-gray-200 text-gray-600'}`}
            >
              Filtri{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
            </button>
          </div>

          {/* Mobile: filtri espandibili */}
          <div className={`lg:hidden overflow-hidden transition-all duration-200 ${filtersOpen ? 'max-h-[520px] opacity-100 mt-2 space-y-2' : 'max-h-0 opacity-0'}`}>
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {['', 'fresco', 'secco', 'fragile'].map((t) => (
                <button
                  key={t || 'tutti'}
                  type="button"
                  onClick={() => setTipo(t)}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border
                    ${tipo === t ? 'bg-[#c78244] text-white border-[#c78244]' : 'border-gray-200 text-gray-600'}`}
                >
                  {t ? TIPO_LABELS[t] : 'Tutti'}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {['', 'nord', 'centro', 'sud', 'isole'].map((z) => (
                <button
                  key={z || 'tutte'}
                  type="button"
                  onClick={() => { setMacroZona(z); if (z) setRegione(''); }}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border
                    ${macroZona === z ? 'bg-[#F6CF48] text-gray-800 border-[#F6CF48]' : 'border-gray-200 text-gray-600'}`}
                >
                  {z ? MACRO_ZONE[z]?.label : 'Italia'}
                </button>
              ))}
            </div>
            <div className="bg-[#f9f6f0] rounded-lg p-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-[#c78244]">📦 Consegna</p>
              <div className="grid grid-cols-3 gap-1.5">
                <input
                  placeholder="Via"
                  value={delivery.via}
                  onChange={(e) => { setDelivery({ ...delivery, via: e.target.value }); setDeliveryApplied(false); }}
                  className="col-span-3 border rounded-md px-2 py-1 text-xs"
                />
                <input
                  placeholder="Città"
                  value={delivery.citta}
                  onChange={(e) => { setDelivery({ ...delivery, citta: e.target.value }); setDeliveryApplied(false); }}
                  className="col-span-2 border rounded-md px-2 py-1 text-xs"
                />
                <input
                  placeholder="CAP"
                  value={delivery.cap}
                  onChange={(e) => { setDelivery({ ...delivery, cap: e.target.value }); setDeliveryApplied(false); }}
                  className="border rounded-md px-2 py-1 text-xs"
                />
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={geolocateDelivery} className="flex-1 text-[11px] border border-[#c78244] text-[#c78244] rounded-md py-1">
                  GPS
                </button>
                <button
                  type="button"
                  onClick={() => { applyDelivery(); setFiltersOpen(false); }}
                  disabled={!delivery.via && !delivery.citta && !delivery.lat}
                  className="flex-[2] text-[11px] bg-[#c78244] text-white rounded-md py-1 disabled:opacity-40"
                >
                  Filtra consegna
                </button>
              </div>
            </div>
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="w-full text-[11px] text-red-600 border border-red-200 rounded-md py-1"
              >
                Reset filtri
              </button>
            )}
          </div>

          {/* Desktop: filtri */}
          <div className="hidden lg:flex flex-col gap-3">
            {/* Prima riga sempre visibile */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="search"
                placeholder="Cerca prodotti…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[180px] border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-[#F6CF48] focus:outline-none"
              />
              {['', 'fresco', 'secco', 'fragile'].map((t) => (
                <button
                  key={t || 'tutti'}
                  type="button"
                  onClick={() => setTipo(t)}
                  className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors
                    ${tipo === t ? 'bg-[#c78244] text-white border-[#c78244]' : 'border-gray-200 text-gray-600 hover:border-[#c78244]'}`}
                >
                  {t ? TIPO_LABELS[t] : 'Tutti i tipi'}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-colors flex items-center gap-2
                  ${filtersOpen ? 'bg-[#c78244] text-white border-[#c78244]' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
              >
                📍 Destinazione & Zone {activeFiltersCount > 0 && !filtersOpen ? `(${activeFiltersCount})` : ''}
              </button>

              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-3 py-2 rounded-xl text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"
                >
                  Reset filtri
                </button>
              )}
            </div>

            {/* Seconda riga a scomparsa (Zone + Spedizione) */}
            <div className={`overflow-hidden transition-all duration-300 ${filtersOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="flex flex-wrap items-start gap-4 p-4 bg-[#f9f6f0] border border-[#f3deb8] rounded-xl shadow-inner mb-2">
                {/* Zone */}
                <div className="flex-1 min-w-[300px]">
                  <p className="text-xs font-semibold text-[#7a4a1f] mb-2">Filtra per macro zona</p>
                  <div className="flex flex-wrap gap-2">
                    {['', 'nord', 'centro', 'sud', 'isole'].map((z) => (
                      <button
                        key={z || 'tutte'}
                        type="button"
                        onClick={() => { setMacroZona(z); if (z) setRegione(''); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                          ${macroZona === z ? 'bg-[#F6CF48] text-gray-800 border-[#F6CF48]' : 'bg-white border-gray-200 text-gray-600 hover:border-[#c78244]'}`}
                      >
                        {z ? MACRO_ZONE[z]?.label : 'Tutta Italia'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Consegna */}
                <div className="flex-[2] min-w-[400px]">
                  <p className="text-xs font-semibold text-[#c78244] mb-2">📦 Verifica disponibilità per destinazione esatta</p>
                  <div className="flex flex-wrap gap-2">
                    <input
                      placeholder="Via"
                      value={delivery.via}
                      onChange={(e) => { setDelivery({ ...delivery, via: e.target.value }); setDeliveryApplied(false); }}
                      className="flex-1 min-w-[120px] border rounded-lg px-3 py-1.5 text-sm"
                    />
                    <input
                      placeholder="Città"
                      value={delivery.citta}
                      onChange={(e) => { setDelivery({ ...delivery, citta: e.target.value }); setDeliveryApplied(false); }}
                      className="w-32 border rounded-lg px-3 py-1.5 text-sm"
                    />
                    <input
                      placeholder="CAP"
                      value={delivery.cap}
                      onChange={(e) => { setDelivery({ ...delivery, cap: e.target.value }); setDeliveryApplied(false); }}
                      className="w-20 border rounded-lg px-3 py-1.5 text-sm"
                    />
                    <button type="button" onClick={geolocateDelivery} className="text-sm bg-white border border-[#c78244] text-[#c78244] rounded-lg px-3 py-1.5 hover:bg-orange-50 transition-colors">
                      GPS
                    </button>
                    <button
                      type="button"
                      onClick={() => { applyDelivery(); setFiltersOpen(false); }}
                      disabled={!delivery.via && !delivery.citta && !delivery.lat}
                      className="text-sm bg-[#c78244] text-white rounded-lg px-4 py-1.5 hover:bg-[#b07137] transition-colors disabled:opacity-40"
                    >
                      Applica
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {catalogoPreview && <PartnerRecruitmentBanner catalogoPreview />}
      {!catalogoPreview && <PartnerRecruitmentBanner compact />}

      {apiError && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
            <strong>Backend non raggiungibile.</strong> {apiError}
            <p className="mt-2 text-xs text-red-700">
              Avvia: <code className="bg-red-100 px-1 rounded">cd newBackendLaravel/backendLar && php artisan serve --port=8000</code>
              {' '}e verifica <code className="bg-red-100 px-1 rounded">frontend/.env.development</code> con <code>VITE_API_BASE_URL=</code> vuoto.
            </p>
          </div>
        </div>
      )}

      {/* Alert senza consegna */}
      {!deliveryApplied && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-3 lg:px-4 pt-2 lg:pt-4"
        >
          <div className="bg-amber-50 border border-amber-200 rounded-lg lg:rounded-xl p-2.5 lg:p-4 text-xs lg:text-sm text-amber-900">
            <strong>Attenzione:</strong> senza destinazione di consegna alcuni prodotti potrebbero non essere disponibili.
            <button
              type="button"
              onClick={() => { setFiltersOpen(true); setTimeout(() => document.querySelector('input[placeholder="Città"]')?.focus(), 100); }}
              className="block mt-1 text-[#c78244] font-semibold underline"
            >
              Imposta destinazione →
            </button>
          </div>
        </motion.div>
      )}

      {deliveryApplied && (
        <div className="max-w-7xl mx-auto px-4 pt-3">
          <div className="flex items-center gap-3 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <img src={camioncino} alt="" className="h-8 w-8 rounded object-cover shrink-0" aria-hidden />
            <p>Mostriamo solo prodotti consegnabili a <strong>{delivery.citta || 'la tua posizione'}</strong></p>
          </div>
        </div>
      )}

      {/* Layout principale: lista + mappa */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6 relative">
        {/* Lista prodotti full-width */}
        <div className="flex-1 space-y-4 min-w-0 z-10">
          <div className="flex items-center justify-between text-xs text-gray-500 px-1">
            <span>
              {loading ? 'Aggiornamento in corso...' : `${categorie.length} risultati`}
            </span>
            <span>Ordina per rilevanza geografica</span>
          </div>

          {/* Banner fuori raggio */}
          {deliveryApplied && (() => {
            const fuoriRaggioCount = categorie.filter(c => c.disponibile === false).length;
            return (
              <>
                {!mostraTutti && fuoriRaggioCount > 0 && (
                  <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 mb-3">
                    <span className="small"><strong>{fuoriRaggioCount}</strong> prodotti non possono arrivarti nella zona impostata.</span>
                    <button type="button" className="btn btn-sm btn-outline-warning ms-3" onClick={() => setMostraTutti(true)}>
                      Mostra anche quelli fuori raggio
                    </button>
                  </div>
                )}
                {mostraTutti && (
                  <div className="alert alert-info d-flex align-items-center justify-content-between py-2 mb-3">
                    <span className="small">Stai vedendo anche prodotti che non possono raggiungerti.</span>
                    <button type="button" className="btn btn-sm btn-outline-info ms-3" onClick={() => setMostraTutti(false)}>
                      Mostra solo disponibili
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {loading ? (
            [1, 2, 3].map((i) => <div key={i} className="mang-card h-36 animate-pulse bg-gray-100" />)
          ) : categorie.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="mb-2">Nessun prodotto trovato con questi filtri.</p>
              {!deliveryApplied && (
                <p className="text-sm">Prova a impostare la destinazione di consegna o allarga i filtri.</p>
              )}
              {regione && (
                <div className="mt-4 p-4 bg-[#fff8ea] border border-[#f3deb8] rounded-xl max-w-md mx-auto text-[#7a4a1f] text-sm">
                  <p className="mb-2">
                    Nessun produttore in <strong>{REGION_LABELS[regione] || regione}</strong> al momento?
                  </p>
                  <Link
                    to={`/candidatura?regione=${regione}`}
                    onClick={() => trackPartnerEvent('partner_cta_click', { source: 'mappa_regione_vuota', regione })}
                    className="text-[#c78244] font-semibold underline"
                  >
                    Candidati da {REGION_LABELS[regione] || regione} →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            categorie.map((c, i) => (
              <ProductRow
                key={c.id}
                index={i}
                categoria={c}
                baseUrl={baseUrl}
                isActive={activeProduct === c.id}
                onVisible={handleVisible}
                onSelect={setSelected}
                preferito={preferitiIds.includes(c.id)}
                onTogglePreferito={togglePreferito}
                interesseOpenId={interesseOpenId}
                setInteresseOpenId={setInteresseOpenId}
                interesseNota={interesseNota}
                setInteresseNota={setInteresseNota}
                interesseEmail={interesseEmail}
                setInteresseEmail={setInteresseEmail}
                interesseInvio={interesseInvio}
                onSegnalaInteresse={segnalaInteresse}
              />
            ))
          )}
        </div>

        {/* Mappa desktop — sticky */}
        <div className="hidden lg:block w-80 xl:w-96 shrink-0">
          <div className="sticky top-[220px]">
            <div className="mang-card p-4 overflow-visible">
              <p className="text-xs text-gray-500 mb-2 text-center">Cartina delle provenienze</p>
              <ItaliaMapView
                activeRegion={mapRegion}
                activeMacro={mapMacro}
                filterRegion={regione}
                filterMacro={macroZona}
                onRegionClick={handleRegionClick}
                onMacroClick={handleMacroClick}
                className="h-80 min-h-[320px]"
              />
              {highlighted && (
                <div className="mt-2 bg-[#fff8ea] border border-[#f3deb8] rounded-lg p-2">
                  <p className="text-center text-xs text-[#c78244] font-semibold">
                    {highlighted.nome}
                  </p>
                  <p className="text-center text-[11px] text-gray-600">
                    Provenienza: {highlighted.provenienza?.nome}
                  </p>
                </div>
              )}
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-2">
              Clicca una regione per filtrare · Scorri la lista per esplorare
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: pill provenienza + cartina fullscreen */}
      <div className="lg:hidden fixed bottom-3 left-3 right-3 z-20">
        <button
          type="button"
          onClick={() => setMobileMapOpen(true)}
          className="w-full mang-card shadow-lg px-4 py-2.5 flex items-center justify-between gap-2 active:scale-[0.98] transition-transform"
        >
          <span className="text-sm font-semibold text-[#c78244] truncate">
            🗺️ {highlighted?.provenienza?.nome || macroLabel(mapMacro)}
          </span>
          <span className="text-[10px] text-gray-400 shrink-0">apri cartina</span>
        </button>
      </div>

      <AnimatePresence>
        {mobileMapOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-50 bg-black/60"
            onClick={() => setMobileMapOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="absolute inset-2 sm:inset-4 bg-[#FFFEFA] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              onClick={() => setMobileMapOpen(false)}
            >
              <div className="shrink-0 px-4 py-3 border-b border-gray-100 text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Provenienza</p>
                <p className="text-lg font-bold text-[#c78244]">
                  {highlighted?.provenienza?.nome || macroLabel(mapMacro)}
                </p>
                {highlighted && (
                  <p className="text-xs text-gray-600 mt-0.5 truncate">{highlighted.nome}</p>
                )}
                <p className="text-[10px] text-gray-400 mt-1">Tocca per chiudere · Clicca una regione per filtrare</p>
              </div>
              <div className="flex-1 min-h-0 p-3" onClick={(e) => e.stopPropagation()}>
                <ItaliaMapView
                  activeRegion={mapRegion}
                  activeMacro={mapMacro}
                  filterRegion={regione}
                  filterMacro={macroZona}
                  onRegionClick={handleRegionClick}
                  onMacroClick={handleMacroClick}
                  expanded
                  showControls
                  className="h-full w-full"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dettaglio prodotto fullscreen */}
      <AnimatePresence>
        {selected && (
          <ProductDetail
            categoria={selected}
            baseUrl={baseUrl}
            delivery={delivery}
            catalogoLive={catalogoLive}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>

      <div className="h-16 lg:hidden" />
    </div>
  );
}

export default function EsperienzaShop() {
  return <EsperienzaShopContent />;
}
